import React, { useState, useRef } from 'react';
import { ArrowLeft, Download, Printer, ChevronRight, Shield, Target, Users, Zap, Map, BarChart, FileText, Lock, Building2, AlertTriangle, BookOpen, Layers, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { writeSanitizedHTML } from '../utils/safePrint';

const WhitepaperView = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState('executive');
  const contentRef = useRef(null);

  const sections = [
    { id: 'executive', label: 'Executive Summary', icon: BookOpen },
    { id: 'challenge', label: 'The Challenge', icon: AlertTriangle },
    { id: 'overview', label: 'Product Overview', icon: Target },
    { id: 'hira', label: 'HIRA', icon: Shield },
    { id: 'builder', label: '17-Step Builder', icon: Layers },
    { id: 'ics', label: 'ICS Compliance', icon: FileText },
    { id: 'capabilities', label: 'Key Capabilities', icon: Zap },
    { id: 'architecture', label: 'Technical Architecture', icon: Building2 },
    { id: 'security', label: 'Security & Access', icon: Lock },
    { id: 'usecases', label: 'Use Cases & Sectors', icon: Users },
    { id: 'conclusion', label: 'Conclusion', icon: CheckCircle },
  ];

  const handlePrint = () => {
    const printContent = contentRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html><head><title>EXRSIM Whitepaper</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1a1a2e; line-height: 1.7; margin: 40px; max-width: 900px; margin: 40px auto; }
        h1 { color: #e97320; font-size: 28px; border-bottom: 3px solid #e97320; padding-bottom: 8px; margin-top: 40px; }
        h2 { color: #1a1a2e; font-size: 22px; margin-top: 32px; }
        h3 { color: #e97320; font-size: 18px; margin-top: 24px; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
        th { background: #e97320; color: white; padding: 10px 12px; text-align: left; }
        td { padding: 8px 12px; border-bottom: 1px solid #ddd; }
        tr:nth-child(even) { background: #f8f8f8; }
        .header { text-align: center; margin-bottom: 50px; }
        .header h1 { font-size: 36px; border: none; color: #e97320; }
        .header p { color: #666; font-size: 16px; }
        .badge { display: inline-block; background: #e97320; color: white; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin: 2px; }
        ul { padding-left: 20px; }
        li { margin-bottom: 6px; }
        .footer { text-align: center; margin-top: 60px; padding-top: 20px; border-top: 2px solid #e97320; color: #888; font-size: 13px; }
        @page { margin: 1in; }
      </style></head><body>
      <div class="header">
        <h1>EXRSIM</h1>
        <p style="font-size:20px; font-weight:600;">Emergency Exercise & Incident Management Platform</p>
        <p>A Canadian Innovation in Emergency Preparedness</p>
        <p style="margin-top:20px; color:#999;">Whitepaper | Version 1.2 | Updated February 2026</p>
        <p style="color:#999;">Ian Clarke, Toronto | Steven Harrison, Winnipeg</p>
      </div>
      ${printContent.innerHTML}
      <div class="footer">
        <p><strong>EXRSIM</strong> &mdash; Plan. Execute. Analyse.</p>
        <p>Copyright 2026 EXRSIM. All rights reserved.</p>
      </div>
      </body></html>
    `);
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const handleDownload = () => {
    window.open('/EXRSIM_Whitepaper.md', '_blank');
  };

  const handleDownloadWord = () => {
    window.open(`${window.location.origin}/api/whitepaper/download`, '_blank');
  };

  return (
    <div className="h-full flex flex-col" data-testid="whitepaper-view">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-900/50 shrink-0">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="outline" onClick={onBack} className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black" data-testid="whitepaper-back">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-orange-500">EXRSIM Whitepaper</h1>
            <p className="text-sm text-gray-400">Emergency Exercise & Incident Management Platform</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadWord} className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black" data-testid="whitepaper-download-word">
            <Download className="h-4 w-4 mr-2" /> Word
          </Button>
          <Button variant="outline" onClick={handleDownload} className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black" data-testid="whitepaper-download">
            <Download className="h-4 w-4 mr-2" /> Markdown
          </Button>
          <Button variant="outline" onClick={handlePrint} className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black" data-testid="whitepaper-print">
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Navigation */}
        <div className="w-64 border-r border-gray-700 bg-gray-900/30 shrink-0 overflow-y-auto">
          <div className="p-4 space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contents</p>
            {sections.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${activeSection === s.id ? 'bg-orange-500/20 text-orange-400 font-medium' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
                  data-testid={`wp-nav-${s.id}`}>
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{s.label}</span>
                  {activeSection === s.id && <ChevronRight className="h-3 w-3 ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto p-8" ref={contentRef}>

            {/* Executive Summary */}
            {activeSection === 'executive' && (
              <div data-testid="wp-section-executive">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Executive Summary</h1>
                <p className="text-gray-300 leading-relaxed mb-4">
                  EXRSIM is a Canadian-developed emergency preparedness and incident management software platform designed to help agencies, organizations, and communities prepare for, respond to, and recover from both natural and man-made emergencies. The software supports the full lifecycle of emergency management, from planning and training to real-world incident response.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  In an era of increasing climate volatility, evolving security threats, and complex interdependencies between critical infrastructure systems, the need for comprehensive emergency preparedness has never been greater. Communities and agencies alike must be better prepared in this ever-changing world of both natural and man-made threats and disasters in order to protect infrastructure, property, and save lives.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  EXRSIM addresses this need by providing a unified, web-based platform that combines exercise design, incident command system (ICS) compliance, real-time collaboration, resource tracking, risk assessment, and analytics into a single, accessible solution. Whether preparing for wildfires, floods, severe weather, industrial incidents, or security events, EXRSIM equips emergency managers with the tools they need to plan effectively, train realistically, and respond decisively.
                </p>
              </div>
            )}

            {/* The Challenge */}
            {activeSection === 'challenge' && (
              <div data-testid="wp-section-challenge">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">The Challenge: Why Emergency Preparedness Matters Now</h1>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">A Changing Threat Landscape</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  The world faces an unprecedented convergence of threats. Natural disasters are increasing in frequency and severity due to climate change. Man-made threats — from industrial accidents to cybersecurity incidents — continue to evolve. Critical infrastructure systems are more interconnected than ever, meaning a single point of failure can cascade across entire regions.
                </p>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">The Preparedness Gap</h2>
                <div className="space-y-3 mb-4">
                  {[
                    'Resource constraints prevent smaller agencies from conducting regular exercises',
                    'Paper-based systems make it difficult to coordinate multi-agency responses',
                    'Lack of standardization leads to communication breakdowns during incidents',
                    'Insufficient training leaves responders unprepared for complex, multi-hazard scenarios',
                    'Poor situational awareness hampers decision-making during rapidly evolving events'
                  ].map((item, i) => (
                    <div key={`wp-${i}`} className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                      <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                      <p className="text-gray-300 text-sm">{item}</p>
                    </div>
                  ))}
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">The Cost of Being Unprepared</h2>
                <p className="text-gray-300 leading-relaxed">
                  The consequences of inadequate preparedness are measured in lives lost, property destroyed, and communities devastated. Effective emergency exercises and incident management tools are not luxuries — they are essential investments in public safety and community resilience. EXRSIM was created to close this preparedness gap by making professional-grade emergency management tools accessible, affordable, and aligned with established standards.
                </p>
              </div>
            )}

            {/* Product Overview */}
            {activeSection === 'overview' && (
              <div data-testid="wp-section-overview">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Product Overview & Value Proposition</h1>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">What EXRSIM Does</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    { title: 'Plan', desc: 'Identify hazards, assess risks, and develop response strategies through structured HIRA processes', icon: Shield },
                    { title: 'Build', desc: 'Design realistic emergency exercises using a streamlined 17-step wizard that produces complete exercise packages', icon: Layers },
                    { title: 'Train', desc: 'Conduct tabletop, functional, and full-scale exercises with real-time collaboration and dynamic scenario injection', icon: Users },
                    { title: 'Respond', desc: 'Transition seamlessly from training mode to operational incident management using the same ICS-compliant tools', icon: Zap },
                    { title: 'Evaluate', desc: 'Capture lessons learned, generate evaluation reports, and track improvement over time through integrated analytics', icon: BarChart },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Card key={`wp-${i}`} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="h-5 w-5 text-orange-500" />
                            <h3 className="font-semibold text-orange-400">{item.title}</h3>
                          </div>
                          <p className="text-gray-300 text-sm">{item.desc}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">Why EXRSIM Matters</h2>
                {[
                  { who: 'For Emergency Managers', text: 'Eliminates the administrative burden of exercise planning and incident documentation. What traditionally takes weeks can be accomplished in hours.' },
                  { who: 'For Agencies & Organizations', text: 'Ensures ICS compliance across all operations, reducing liability and improving interoperability during multi-agency responses.' },
                  { who: 'For Communities', text: 'Makes professional emergency management tools accessible, enabling communities of all sizes to build resilience. Better preparedness means faster response times and lives saved.' },
                  { who: 'For Decision-Makers', text: 'Comprehensive analytics and reporting provide data to justify investments, satisfy regulatory requirements, and demonstrate continuous improvement to stakeholders and grant providers.' },
                ].map((item, i) => (
                  <div key={`wp-${i}`} className="mb-3 pl-4 border-l-2 border-orange-500">
                    <p className="text-orange-400 font-semibold text-sm">{item.who}</p>
                    <p className="text-gray-300 text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
            )}

            {/* HIRA */}
            {activeSection === 'hira' && (
              <div data-testid="wp-section-hira">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Hazard Identification & Risk Assessment (HIRA)</h1>
                <p className="text-gray-300 leading-relaxed mb-4">
                  One of the foundational elements of EXRSIM is its support for the Hazard Identification and Risk Assessment (HIRA) process. By systematically identifying potential hazards and assessing associated risks, organizations can better understand their vulnerabilities and prepare appropriate response strategies before an incident occurs.
                </p>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">HIRA Capabilities</h2>
                <div className="space-y-3 mb-6">
                  {[
                    { title: 'Hazard Cataloguing', desc: 'Identify and classify potential hazards across all threat categories (natural, technological, human-caused)' },
                    { title: 'Risk Scoring', desc: 'Evaluate each hazard based on probability, impact severity, and community vulnerability using standardized criteria' },
                    { title: 'Vulnerability Mapping', desc: 'Identify specific assets, populations, and infrastructure at risk from each identified hazard' },
                    { title: 'Mitigation Planning', desc: 'Develop and document risk reduction strategies linked to specific hazards' },
                    { title: 'Priority Ranking', desc: 'Automatically rank risks to focus preparedness efforts on the most critical threats' },
                  ].map((item, i) => (
                    <div key={`wp-${i}`} className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <CheckCircle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-orange-400 font-semibold text-sm">{item.title}</p>
                        <p className="text-gray-300 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Card className="bg-gray-800/50 border-orange-500/30">
                  <CardContent className="p-5">
                    <h3 className="text-orange-400 font-semibold mb-2">From Assessment to Action</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      EXRSIM's HIRA module feeds directly into the exercise building process. Identified hazards and risk scenarios become the foundation for realistic training exercises, ensuring that organizations train against the threats most relevant to their community. This closed-loop approach between assessment and training is a key differentiator of the EXRSIM platform.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* 17-Step Builder */}
            {activeSection === 'builder' && (
              <div data-testid="wp-section-builder">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">The 17-Step Exercise & Event Builder</h1>

                <Card className="bg-gray-800/50 border-orange-500/30 mb-6">
                  <CardContent className="p-5">
                    <h3 className="text-orange-400 font-semibold mb-2">From Exercise to Operations</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      A guided wizard ensures comprehensive exercise design while remaining intuitive enough for users at any experience level. Every list-bearing step (Goals, MSEL, Organizations, Sites, Coordinators, Code Words, Callsigns, Assumptions, Artificialities, Safety, Standards) supports per-card expand/collapse plus Expand All / Collapse All so ExCon can navigate complex packets at a glance. Once the minimum required information has been entered, the Incident Commander can establish an Incident Command Team and begin coordinating an effective response. The same platform used for training exercises can be used for real-world incident management, ensuring familiarity and reducing response times when it matters most.
                    </p>
                  </CardContent>
                </Card>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="px-3 py-2 text-left rounded-tl-lg w-12">Step</th>
                        <th className="px-3 py-2 text-left w-40">Name</th>
                        <th className="px-3 py-2 text-left rounded-tr-lg">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['1', 'Exercise Details', 'Define name, type (TTX, Functional, Full-Scale, Drill, Workshop), date, duration, location, and imagery'],
                        ['2', 'Scenario', 'Describe the simulated incident and hazard type'],
                        ['3', 'Purpose', 'Articulate the intent and reason for conducting the exercise'],
                        ['4', 'Scope', 'Establish boundaries, hazards, geographic area, functions, and exercise type'],
                        ['5', 'Goals & Objectives', 'Set high-level goals with a highlighted Broad Statement and per-goal SMART objectives'],
                        ['6', 'Functions', 'Select ICS functions to be exercised (Command, Operations, Planning, Logistics, Finance/Admin)'],
                        ['7', 'Injections (MSEL)', 'Create Master Scenario Events List entries with auto-numbered Event #, HH:MM scenario time, event type, inject mode, from → to and expected response'],
                        ['8', 'Organizations', 'Add participating agencies plus a nested Persons roster per organization'],
                        ['9', 'Exercise Sites', 'Attach imagery, coordinates, exercise function and participant guidance for each physical site'],
                        ['10', 'Team Coordinators', 'Assign the exercise coordination team with role, contact and area assignment'],
                        ['11', 'Code Words', 'Define exercise start, pause, and stop code words'],
                        ['12', 'Callsigns & Frequencies', 'Configure communication callsigns and radio frequencies for the exercise'],
                        ['13', 'Assumptions', 'Document exercise assumptions and ground rules'],
                        ['14', 'Artificialities', 'List artificial constraints that differ from real-world operations'],
                        ['15', 'Safety Concerns', 'Capture safety requirements, medical plans, and emergency procedures'],
                        ['16', 'Standard of Conduct', 'State the behavioural expectations for every participant, controller and evaluator'],
                        ['17', 'Mapping', 'Create map objects (markers, polygons, routes) for the exercise area using an integrated Leaflet map'],
                      ].map(([step, name, desc], i) => (
                        <tr key={`wp-${i}`} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/60'}>
                          <td className="px-3 py-2 text-orange-400 font-bold">{step}</td>
                          <td className="px-3 py-2 text-white font-medium">{name}</td>
                          <td className="px-3 py-2 text-gray-300">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Card className="bg-gray-800/50 border-orange-500/30 mt-6">
                  <CardContent className="p-5">
                    <h3 className="text-orange-400 font-semibold mb-2">One-Click Reports</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      A single "Print Complete Exercise" button generates a fully sanitized, DOM-safe HTML packet covering every one of the 17 steps — including live map objects fetched at print time — with counts per section and consistent formatting suited for briefing binders.
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ICS Compliance */}
            {activeSection === 'ics' && (
              <div data-testid="wp-section-ics">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">ICS Compliance & Form System</h1>
                <p className="text-gray-300 leading-relaxed mb-6">
                  The software is designed to align with the Canadian Incident Command System (ICS), ensuring compatibility with established emergency management practices used across Canada and internationally.
                </p>

                <h2 className="text-xl font-semibold text-white mt-4 mb-4">Complete ICS Form Library</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="px-3 py-2 text-left rounded-tl-lg w-20">Form</th>
                        <th className="px-3 py-2 text-left w-56">Name</th>
                        <th className="px-3 py-2 text-left rounded-tr-lg">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['ICS 201', 'Incident Briefing', 'Initial incident overview and resource summary'],
                        ['ICS 202', 'Incident Objectives', 'Operational period objectives and priorities'],
                        ['ICS 203', 'Organization Assignment List', 'ICS structure and personnel assignments'],
                        ['ICS 204', 'Assignment List', 'Division/group tactical assignments'],
                        ['ICS 205', 'Radio Communications Plan', 'Radio frequency assignments'],
                        ['ICS 206', 'Medical Plan', 'Medical aid stations and procedures'],
                        ['ICS 207', 'Organization Chart', 'Visual ICS structure'],
                        ['ICS 208', 'Safety Message/Plan', 'Safety messages and hazard mitigations'],
                        ['ICS 213', 'General Message', 'Standardized message format'],
                        ['ICS 214', 'Activity Log', 'Unit or individual activity documentation'],
                        ['ICS 218', 'Vehicle/Equipment Inventory', 'Vehicle and equipment tracking'],
                        ['ICS 220', 'Air Operations Summary', 'Aviation resource management'],
                        ['ICS 259', 'Financial Status Summary', 'Cost tracking: Crew, Equipment, Overhead, Supplies'],
                        ['ICS 309', 'Communications Log', 'Radio and communications record'],
                      ].map(([form, name, purpose], i) => (
                        <tr key={`wp-${i}`} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/60'}>
                          <td className="px-3 py-2 text-orange-400 font-mono font-bold">{form}</td>
                          <td className="px-3 py-2 text-white">{name}</td>
                          <td className="px-3 py-2 text-gray-300">{purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">Logistics & Finance Unit Forms</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { name: 'Food Unit', desc: 'Meal planning, dietary requirements, and service scheduling' },
                    { name: 'Facilities Unit', desc: 'Base camp, staging area, and facility management' },
                    { name: 'Compensation/Claims', desc: 'Injury compensation and property damage documentation' },
                    { name: 'Cost Unit', desc: 'Expense tracking and analysis across all cost categories' },
                    { name: 'Time Unit', desc: 'Personnel time recording, overtime, and hazard pay' },
                    { name: 'Procurement', desc: 'Purchasing, vendor management, and supply chain tracking' },
                  ].map((item, i) => (
                    <div key={`wp-${i}`} className="flex items-start gap-2 bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                      <FileText className="h-4 w-4 text-orange-500 shrink-0 mt-1" />
                      <div>
                        <p className="text-orange-400 font-semibold text-sm">{item.name}</p>
                        <p className="text-gray-400 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Capabilities */}
            {activeSection === 'capabilities' && (
              <div data-testid="wp-section-capabilities">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Key Platform Capabilities</h1>

                {[
                  { title: 'Real-Time Collaboration', icon: Users, text: 'Multiple users work simultaneously on incident management tasks using real-time Socket.IO communication. Changes are instantly reflected for all connected team members, ensuring everyone operates from a common operational picture.' },
                  { title: 'AI Deputy IC (Canadian ICS)', icon: Zap, text: 'A built-in conversational assistant powered by Claude Sonnet 4.5 and grounded exclusively in the Canadian Incident Command System (ICS Canada, BCWS, CIFFC, Public Safety Canada FERP). Available as a floating panel on every ICS-section page, it reads a live Situation Snapshot of the current exercise (objectives, assignments, org chart, resources, safety findings, schedule, activity log) and helps commanders draft SMART objectives, audit span of control, scan for safety risks, summarize activity, and produce verbal IAP briefings — each answer cites the relevant ICS form or principle. Conversations are private per (exercise + user).' },
                  { title: 'Integrated Mapping & Situational Awareness', icon: Map, text: 'Interactive Leaflet maps with satellite, terrain, and street views. Drawing tools for incident perimeters, hazard zones, evacuation routes, and points of interest. Real-time weather data overlays and resource position visualization.' },
                  { title: 'Weather Obs, WX Report & Severe Wx (Winlink CSV)', icon: AlertTriangle, text: 'A dedicated Tech Spec Unit ships three complementary weather modules: (1) auto-migrated GOES satellite Weather Obs feeds (self-healing on startup), (2) a KF5SMH v4.0.6-compliant WX Report form with rigid 24-hour time and LAT/LON validators, and (3) a Severe Wx module for warning/emergency conditions. Both weather reports accept "Import from Winlink CSV" uploads, parsing amateur-radio-formatted files and validating every field before ingest so ExCon can absorb field observations without retyping.' },
                  { title: 'Real Event Builder from HIRA', icon: Shield, text: 'Every HIRA entry can be promoted with one click into a Real Event via a dedicated "Real Event" button on the entry card — the platform inherits the hazard\'s name, imagery and risk profile so response operations spin up in seconds rather than hours.' },
                  { title: 'Two-Tier Resource Tracking', icon: Target, text: 'Resources are modelled in two tiers: Parent Resource Types (templates across 57 categories — vehicles, equipment, personnel, aircraft, watercraft, specialized assets) and Individual Units (concrete deployable instances with their own callsign/Unit ID, status, and location). Track each unit through its full lifecycle: Available, Assigned, En Route, On Scene, and Out of Service. Quick Assign on ICS 204 and ICS 230 pulls directly from Individual Units.' },
                  { title: 'Interactive Daily Schedule (ICS 230)', icon: BarChart, text: 'A full Day/Week/Month/Year interactive calendar built on react-big-calendar. Each meeting carries a Date/Time From-To range, category colour, attendee chip-list, location, and Quick-Assigned resources. One-click export to iCal (.ics) or Google Calendar means schedules can flow directly to commanders\' personal calendars.' },
                  { title: 'Analytics Dashboard', icon: BarChart, text: 'Built-in analytics with Resource Allocation by Type bar charts (counted by Individual Units for accurate deployable strength), Personnel Distribution by Role visualization across ICS sections (Command, Operations, Planning, Logistics, Finance), and exercise metrics including participation rates and operational tempo.' },
                  { title: 'MSEL — Master Scenario Events List', icon: Zap, text: 'Auto-numbered Event # increments from the highest existing event so ExCon never has to hand-count. Scenario time is captured strictly as 24-hour HH:MM (validated on entry, stored consistently, printed identically). The list view supports per-card expand/collapse with a collapsed row showing Event #, title, event type, date/time and the Mark Complete status pill — plus Expand All / Collapse All for busy exercises.' },
                  { title: 'Exercise Evaluations (9 ICS Key Areas)', icon: CheckCircle, text: 'Evaluations are captured across nine ICS-aligned key areas (Command & Control, Operations, Planning, Logistics, Finance/Admin, Communication, Resource Management, Safety & Security, Training & Readiness) with accessible colour-coded radio buttons (Excellent → Unacceptable + Not Assessed). Overall Rating excludes "Not Assessed" categories from its average so scores stay honest. Each Key Area assessment prints on its own full-width row for briefing binders.' },
                  { title: 'Help Desk & Support', icon: Shield, text: 'Built-in ticket system for support. Users submit tickets with subject, description, recommendation, and priority. Full Create-Read-Update-Delete capabilities for ticket owners. Administrators have oversight with report printing. 65+ in-app FAQ entries across 25 categories spanning ICS forms, Resource Management, AI Deputy IC, User Management, Mapping, Weather modules, and Logistics.' },
                  { title: 'Security & Administration', icon: Lock, text: 'Role-based access control (Super Admin, Agency Admin, User), Agency-Admin and Super-Admin 2FA toggle per user, hardened secureStorage with in-memory fallback, case-insensitive email login (with idempotent startup migration to heal legacy mixed-case accounts), and complete separation of test secrets from source code via environment variables.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={`wp-${i}`} className="mb-5 bg-gray-800/50 rounded-lg p-5 border border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="h-5 w-5 text-orange-500" />
                        <h2 className="text-lg font-semibold text-white">{item.title}</h2>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Technical Architecture */}
            {activeSection === 'architecture' && (
              <div data-testid="wp-section-architecture">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Technical Architecture</h1>
                <p className="text-gray-300 leading-relaxed mb-6">
                  EXRSIM is built on a modern, scalable technology stack designed for reliability, performance, and security.
                </p>

                <h2 className="text-xl font-semibold text-white mt-4 mb-4">Technology Stack</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="px-3 py-2 text-left rounded-tl-lg w-32">Layer</th>
                        <th className="px-3 py-2 text-left w-48">Technology</th>
                        <th className="px-3 py-2 text-left rounded-tr-lg">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Frontend', 'React.js', 'Component-based user interface with responsive design'],
                        ['UI Framework', 'Shadcn/UI + Tailwind CSS', 'Modern, accessible component library'],
                        ['Backend API', 'FastAPI (Python)', 'High-performance REST API with automatic documentation'],
                        ['Real-Time', 'Socket.IO', 'Bidirectional communication for live collaboration'],
                        ['Database', 'MongoDB', 'Flexible document database for complex ICS data'],
                        ['Mapping', 'Leaflet + React-Leaflet', 'Interactive mapping with drawing tools and layers'],
                        ['Charts', 'Recharts', 'Data visualization for analytics dashboards'],
                        ['Authentication', 'JWT', 'Stateless, secure token-based authentication'],
                      ].map(([layer, tech, purpose], i) => (
                        <tr key={`wp-${i}`} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/60'}>
                          <td className="px-3 py-2 text-orange-400 font-semibold">{layer}</td>
                          <td className="px-3 py-2 text-white">{tech}</td>
                          <td className="px-3 py-2 text-gray-300">{purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">Design Principles</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { title: 'Cloud-Native', desc: 'Designed for modern cloud infrastructure with containerization support' },
                    { title: 'Responsive Design', desc: 'Accessible from desktop, tablet, and mobile devices' },
                    { title: 'API-First', desc: 'RESTful API enables integration with existing emergency management systems' },
                    { title: 'Modular Architecture', desc: '64+ component files organized for maintainability and extensibility' },
                    { title: 'Real-Time by Default', desc: 'All collaborative features operate in real-time with WebSockets' },
                  ].map((item, i) => (
                    <div key={`wp-${i}`} className="flex items-start gap-2 bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                      <CheckCircle className="h-4 w-4 text-orange-500 shrink-0 mt-1" />
                      <div>
                        <p className="text-white font-semibold text-sm">{item.title}</p>
                        <p className="text-gray-400 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Security */}
            {activeSection === 'security' && (
              <div data-testid="wp-section-security">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Security & Access Control</h1>

                <h2 className="text-xl font-semibold text-white mt-4 mb-3">Authentication</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  EXRSIM implements industry-standard authentication with secure password hashing via bcrypt, token-based session management, configurable session expiration, and protection against common attack vectors.
                </p>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">Role-Based Access Control (RBAC)</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="px-3 py-2 text-left rounded-tl-lg w-44">Role</th>
                        <th className="px-3 py-2 text-left rounded-tr-lg">Capabilities</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Super Admin', 'Full system access, user management, agency management, all tickets, system configuration'],
                        ['Admin', 'Exercise management, ICS operations, resource management, analytics'],
                        ['User', 'Exercise participation, form completion, ticket submission'],
                        ['Exercise Controller', 'Scenario injection, exercise monitoring, evaluation access'],
                      ].map(([role, caps], i) => (
                        <tr key={`wp-${i}`} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/60'}>
                          <td className="px-3 py-2 text-orange-400 font-semibold">{role}</td>
                          <td className="px-3 py-2 text-gray-300">{caps}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Use Cases */}
            {activeSection === 'usecases' && (
              <div data-testid="wp-section-usecases">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Use Cases & Applicable Sectors</h1>

                <h2 className="text-xl font-semibold text-white mt-4 mb-4">Exercise Types Supported</h2>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="px-3 py-2 text-left rounded-tl-lg w-40">Type</th>
                        <th className="px-3 py-2 text-left w-64">Description</th>
                        <th className="px-3 py-2 text-left rounded-tr-lg">Best For</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Tabletop (TTX)', 'Discussion-based exercise exploring response to a scenario', 'Policy review, role familiarization, plan validation'],
                        ['Functional', 'Simulated incident with activated command structures', 'ICS proficiency, communications testing, coordination'],
                        ['Full-Scale', 'Complete deployment of personnel and resources', 'Operational readiness, multi-agency coordination'],
                        ['Drill', 'Focused practice of a single function or procedure', 'Skill maintenance, equipment familiarization'],
                        ['Workshop', 'Collaborative session to develop or refine plans', 'Plan development, SOP creation, team building'],
                      ].map(([type, desc, best], i) => (
                        <tr key={`wp-${i}`} className={i % 2 === 0 ? 'bg-gray-800/30' : 'bg-gray-800/60'}>
                          <td className="px-3 py-2 text-orange-400 font-semibold">{type}</td>
                          <td className="px-3 py-2 text-white">{desc}</td>
                          <td className="px-3 py-2 text-gray-300">{best}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-4">Applicable Sectors</h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    'Municipal Emergency Management', 'Fire Services', 'Emergency Medical Services',
                    'Law Enforcement', 'Transportation', 'Energy & Utilities', 'Healthcare',
                    'Education', 'Industrial & Manufacturing', 'Indigenous Communities',
                    'Non-Governmental Organizations', 'Private Sector'
                  ].map((sector, i) => (
                    <Badge key={`wp-${i}`} className="bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30 transition-colors">{sector}</Badge>
                  ))}
                </div>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">Threat Scenarios</h2>
                <p className="text-gray-300 text-sm mb-3">
                  The software is equally applicable to incidents caused by natural disasters — such as wildfires, floods, and severe weather — as well as man-made events:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    'Wildfire interface and evacuation', 'Flood response and water rescue',
                    'Severe weather (ice storms, tornadoes)', 'Hazardous materials spills',
                    'Mass casualty incidents', 'Infrastructure failure (power, water)',
                    'Public safety and security events', 'Pandemic and public health',
                    'Cyber incidents on critical infra', 'Transportation accidents',
                  ].map((item, i) => (
                    <div key={`wp-${i}`} className="flex items-center gap-2 text-gray-300 text-sm bg-gray-800/30 rounded px-3 py-2">
                      <AlertTriangle className="h-3 w-3 text-orange-500 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conclusion */}
            {activeSection === 'conclusion' && (
              <div data-testid="wp-section-conclusion">
                <h1 className="text-3xl font-bold text-orange-500 mb-6">Conclusion</h1>

                <h2 className="text-xl font-semibold text-white mt-4 mb-3">Canadian Innovation for Global Resilience</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  EXRSIM represents a significant advancement in emergency management technology. By combining comprehensive exercise design, ICS-compliant incident management, real-time collaboration, and data-driven analytics in a single platform, EXRSIM enables organizations of all sizes to build meaningful emergency preparedness capabilities.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  The successful use of EXRSIM underscores the value of combining trusted communications expertise with modern emergency management tools. As emergencies become more complex and interconnected, these kinds of integrated solutions will only grow in importance.
                </p>

                <h2 className="text-xl font-semibold text-white mt-6 mb-3">The Path Forward</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  In a world where the question is not <em>if</em> an emergency will occur but <em>when</em>, preparedness is the single most effective investment a community can make. EXRSIM provides the tools to move from reactive response to proactive preparedness — protecting infrastructure, property, and most importantly, saving lives.
                </p>

                <Card className="bg-gradient-to-r from-orange-500/20 to-orange-600/10 border-orange-500/40">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-2xl font-bold text-orange-400 mb-2">EXRSIM</h3>
                    <p className="text-gray-300 italic text-lg mb-3">Plan. Execute. Analyse.</p>
                    <p className="text-gray-400 text-sm">A Canadian-developed platform for emergency preparedness and incident management.</p>
                    <p className="text-gray-500 text-xs mt-4">Copyright 2026 EXRSIM. All rights reserved.</p>
                    <p className="text-gray-500 text-[10px] mt-1">Whitepaper · Version 1.2 · Last updated February 2026</p>
                  </CardContent>
                </Card>
              </div>
            )}

          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default WhitepaperView;
